import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';

import assertEventType from '../../../../../common/bento/lib/assertEventType';
import getIsAddressFilled from '../../../lib/getIsAddressFilled';
import { SetCapitalInfoCapitalDepositQuestionsDocument } from '../graphql/mutations/setCapitalInfoCapitalDepositQuestions.gql';
import { SetNoPartnerCapitalDepositQuestionsDocument } from '../graphql/mutations/setNoPartnerCapitalDepositQuestions.gql';
import { SetPartnerCapitalDepositQuestionsDocument } from '../graphql/mutations/setPartnerCapitalDepositQuestions.gql';
import { SubmitCapitalDepositQuestionsDocument } from '../graphql/mutations/submitCapitalDepositQuestions.gql';
import { UpdateCompanyAddressCapitalDepositQuestionsDocument } from '../graphql/mutations/updateCompanyAddressCapitalDepositQuestions.gql';
import { UpdateCompanyAddressSameCapitalDepositQuestionsDocument } from '../graphql/mutations/updateCompanyAddressSameCapitalDepositQuestions.gql';
import { UpdateCompanyLegalInfoCapitalDepositQuestionsDocument } from '../graphql/mutations/updateCompanyLegalInfoCapitalDepositQuestions.gql';
import { Model } from './model';

export enum Service {
  SendCapitalDepositQuestions = 'sendCapitalDepositQuestions',
  SendCapitalInfo = 'sendCapitalInfo',
  SendCompanyAddress = 'sendCompanyAddress',
  SendCompanyLegalInfo = 'sendCompanyLegalInfo',
  SendHasNoPartner = 'sendHasNoPartner',
  SendPartner = 'sendPartner',
  SendSameAddress = 'sendSameAddress',
}

const services: MachineServices<Model, Service> = {
  sendCapitalDepositQuestions: (context, event) => {
    assertEventType(event, 'SUBMIT_CAPITAL_DEPOSIT_QUESTIONS');

    const { capitalDepositId } =
      context.getModuleData().data.viewer.company.capitalDeposit;

    return apolloClient.mutate({
      mutation: SubmitCapitalDepositQuestionsDocument,
      variables: {
        capitalDepositId,
      },
    });
  },
  sendCapitalInfo: (context, event) => {
    assertEventType(event, 'SUBMIT_CAPITAL_INFO');

    const { companyProfileId } = context.getModuleData().data.viewer.company;
    const { legalNumberOfShares, legalShareCapital, paidUpCapitalPercentage } =
      event.data;

    return apolloClient.mutate({
      mutation: SetCapitalInfoCapitalDepositQuestionsDocument,
      variables: {
        input: {
          companyProfileId,
          legalNumberOfShares,
          legalShareCapital,
          paidUpCapitalPercentage,
        },
      },
    });
  },
  sendCompanyAddress: (context, event) => {
    assertEventType(event, 'SUBMIT_COMPANY_ADDRESS');

    const { company } = context.getModuleData().data.viewer;
    const { companyProfileId } = company;
    const { city, street, streetAddition, zip } = event.data;

    return apolloClient.mutate({
      mutation: UpdateCompanyAddressCapitalDepositQuestionsDocument,
      variables: {
        input: {
          city,
          companyProfileId,
          street,
          streetAddition,
          zip,
        },
      },
    });
  },
  sendCompanyLegalInfo: (context, event) => {
    assertEventType(event, 'SUBMIT_COMPANY_LEGAL_INFO');

    const { companyProfileId } = context.getModuleData().data.viewer.company;

    return apolloClient.mutate({
      mutation: UpdateCompanyLegalInfoCapitalDepositQuestionsDocument,
      variables: { input: { ...event.data, companyProfileId } },
    });
  },
  sendHasNoPartner: (context, event) => {
    assertEventType(event, 'SUBMIT_HAS_PARTNER');

    const { companyProfileId } = context.getModuleData().data.viewer.company;

    return apolloClient.mutate({
      mutation: SetNoPartnerCapitalDepositQuestionsDocument,
      variables: {
        input: {
          companyProfileId,
          partner: '',
        },
      },
    });
  },
  sendPartner: (context, event) => {
    assertEventType(event, 'SUBMIT_PARTNER_NAME');

    const { companyProfileId } = context.getModuleData().data.viewer.company;
    const { partnerName } = event.data;

    return apolloClient.mutate({
      mutation: SetPartnerCapitalDepositQuestionsDocument,
      variables: {
        input: {
          companyProfileId,
          partner: partnerName,
        },
      },
    });
  },
  sendSameAddress: (context, event) => {
    assertEventType(event, 'SUBMIT_SAME_ADDRESS');

    const { company, profile: userProfile } =
      context.getModuleData().data.viewer;
    const { companyProfileId } = company;

    if (!getIsAddressFilled(userProfile)) {
      throw Error('Incomplete address in sendSameAddress');
    }

    const { city, street, streetAddition, zip } = userProfile;

    return apolloClient.mutate({
      mutation: UpdateCompanyAddressSameCapitalDepositQuestionsDocument,
      variables: {
        input: {
          city,
          companyProfileId,
          street,
          streetAddition,
          zip,
        },
      },
    });
  },
};

export default services;
